import {useContext, useEffect, useState} from 'react';
import {ChatParams, webEventsApi} from '../../../../../../amplitude/webEvents';
import {BotData} from '../../../../../../api/types';
import {AppContext} from '../../../../../../App';
import {api, Message} from '../../../../../../common-lib/src/api/Api';
import {CommonDialog} from '../CommonDialog/CommonDialog';
import styles from './BotMemories.module.css';
import {prepareObjForAnalitics} from '../utils';
import {CV4RMemory} from '../../../../../../common-lib/src/api/Api.types';
import {Memories, MemoryMessage} from '../MemoryMessage/MemoryMessage';
import {Spinner} from '../../../../../../common-lib/src/components';
import addIcon from './images/add.svg';
import {AddBotMemories} from '../AddBotMemory/AddBotMemories';
type Props = {
	bot: BotData;
	onClose: () => void;
	message?: Message;
};

type UseMemoriesProps = {
	bot: BotData;
	message?: Message;
	refetch?: () => void;
	source: 'all_memories' | 'message_memory';
};

export const useMemories = ({
	bot,
	message,
	refetch,
	source,
}: UseMemoriesProps) => {
	const {user, setIsPaywallModalOpened, setPaywallType, setIsLoginOpened} =
		useContext(AppContext);

	const handleEdit = () => {
		if (!user || user?.isAnon) {
			setIsLoginOpened(true);
			return;
		}
		if (user?.accessLevel !== 'ultra') {
			setPaywallType('ultra');
			setIsPaywallModalOpened(true);
			return;
		}
		const analiticsParams: ChatParams & {
			source?: 'all_memories' | 'message_memory';
		} = prepareObjForAnalitics(bot, user, message);
		const updatedAnaliticsParams = {
			...analiticsParams,
			source,
		} as ChatParams & {source: 'all_memories' | 'message_memory'};
		webEventsApi.memoryEdited(updatedAnaliticsParams);
		refetch?.();
	};

	const handleDelete = () => {
		if (!user || user?.isAnon) {
			setIsLoginOpened(true);
			return;
		}
		if (user?.accessLevel !== 'ultra') {
			setPaywallType('ultra');
			setIsPaywallModalOpened(true);
			return;
		}
		const analiticsParams: ChatParams & {
			source?: 'all_memories' | 'message_memory';
		} = prepareObjForAnalitics(bot, user, message);
		const updatedAnaliticsParams = {
			...analiticsParams,
			source,
		} as ChatParams & {source: 'all_memories' | 'message_memory'};
		webEventsApi.memoryDeleted(updatedAnaliticsParams);
		refetch?.();
	};

	return {
		handleEdit,
		handleDelete,
	};
};

export const BotMemories = ({bot, onClose, message}: Props) => {
	const {user, setIsPaywallModalOpened, setPaywallType, setIsLoginOpened} =
		useContext(AppContext);
	const [isLoading, setIsLoading] = useState(true);
	const [memories, setMemories] = useState<CV4RMemory[]>([]);
	const [filteredMemories, setFilteredMemories] = useState<CV4RMemory[]>([]);
	const [search, setSearch] = useState('');
	const [isAddMemoryOpen, setIsAddMemoryOpen] = useState(false);
	useEffect(() => {
		webEventsApi.allMemoriesAppear(prepareObjForAnalitics(bot, user, message));
	}, []);

	useEffect(() => {
		api.getMemoryItems(bot.id).then((memories) => {
			setMemories(memories);
			setIsLoading(false);
		});
	}, []);

	const handleRefetch = async () => {
		setIsLoading(true);
		try {
			const memories = await api.getMemoryItems(bot.id);
			setMemories(memories);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		setFilteredMemories(
			memories.filter((memory) =>
				memory.text.toLowerCase().includes(search.toLowerCase())
			)
		);
	}, [search, memories]);

	useEffect(() => {
		setSearch('');
	}, [onClose]);

	const {handleEdit, handleDelete} = useMemories({
		bot,
		message,
		refetch: handleRefetch,
		source: 'all_memories',
	});

	const handleAddMemory = (withRefetch: boolean) => {
		setIsAddMemoryOpen(false);
		if (withRefetch) {
			handleRefetch();
			webEventsApi.memoryCreated(prepareObjForAnalitics(bot, user, message));
		}
	};

	const handleOpenAddMemory = () => {
		if (!user || user?.isAnon) {
			setIsLoginOpened(true);
			return;
		}
		if (user?.accessLevel !== 'ultra') {
			setPaywallType('ultra');
			setIsPaywallModalOpened(true);
			return;
		}
		setIsAddMemoryOpen(true);
		webEventsApi.memoryCreated(prepareObjForAnalitics(bot, user, message));
	};

	return (
		<CommonDialog title="All memories" onClose={onClose}>
			<div className={styles.container}>
				<div className={styles.header}>
					<div className={styles.search}>
						<input
							type="text"
							placeholder="Search"
							className={styles.searchInput}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							disabled={isLoading}
						/>
					</div>
					<div className={styles.addMemory} onClick={handleOpenAddMemory}>
						<img src={addIcon} alt="add" />
					</div>
				</div>
				{isLoading ? (
					<Spinner withLayout={false} />
				) : (
					<Memories
						memory={filteredMemories}
						handleEdit={handleEdit}
						handleDelete={handleDelete}
						searchQuery={search}
					/>
				)}
			</div>
			{isAddMemoryOpen && (
				<AddBotMemories botId={bot.id} onClose={handleAddMemory} />
			)}
		</CommonDialog>
	);
};
